import { Alert, Typography } from "@mui/material";
import { Stack } from "@mui/system";
import { AxiosError } from "axios";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import SearchEdit from "../components/inputs/SearchEdit";
import KlasisLoader from "../components/KlasisLoader";
import SearchResults from "../components/SearchResults";
import { ApiRoutes } from "../configuration/apiRoutes";
import { defaultLocale } from "../constants/languages";
import SEARCH_PARAMS from "../constants/searchParams";
import { Translations } from "../localization/translations";
import { SearchResponse } from "../models/SearchResponse";
import PageContent from "../parts/page-content/PageContent";
import { RestService } from "../utils/axios";
import useKlasisSearchNavigator from "../utils/klasisSearchNavigator";
import useKlasisTracker from "../utils/klasisTracker";

const GlobalSearch: React.FC = () => {
    const { t } = useTranslation();

    const search = useLocation().search;
    const searchParams = new URLSearchParams(search);
    const searchPhrase = searchParams.get(SEARCH_PARAMS.searchPhrase);
    const pageNumber = parseInt(searchParams.get(SEARCH_PARAMS.page) ?? "1");

    const [isLoading, setIsLoading] = useState<boolean>(false);

    const { klasisTrackPageView, klasisTrackSiteSearch } = useKlasisTracker();
    const { klasisSearchNavigate } = useKlasisSearchNavigator();
    const navigate = useNavigate();

    const [data, setData] = useState<SearchResponse>();
    const [internalSearchPhrase, setInternalSearchPhrase] =
        useState<string>("");

    const { locale } = useParams();

    useEffect(() => {
        klasisTrackPageView();
    }, []);

    useEffect(() => {
        setInternalSearchPhrase(searchPhrase ?? "");

        const refreshData = () => {
            if (!searchPhrase) return;

            setIsLoading(true);

            RestService.Get<SearchResponse>(
                ApiRoutes.Search.GetGlobalResults({
                    localeCode: locale ?? defaultLocale,
                    pageNumber: pageNumber,
                    searchPhrase: searchPhrase,
                }),
                (response: SearchResponse) => {
                    setData(response);

                    klasisTrackSiteSearch({
                        searchPhrase: searchPhrase,
                        requestId: response.requestId,
                    });
                },
                (error: AxiosError) => {
                    console.log(`Error: ${error.message}`);
                },
                () => {
                    setIsLoading(false);
                }
            );
        };

        refreshData();
    }, [searchPhrase, pageNumber, locale]);

    const handlePageChange = (pageNumber: number): void => {
        klasisSearchNavigate(searchPhrase ?? "", pageNumber);
    };

    const renderSearchResults = (): JSX.Element => {
        return (
            <>
                <SearchEdit
                    placeholder={t(Translations.common.label.search)}
                    value={internalSearchPhrase}
                    onSearch={(phrase: string) => {
                        klasisSearchNavigate(phrase);
                    }}
                    onChange={(event) => {
                        setInternalSearchPhrase(event.target.value);
                    }}
                    onSeachClear={() => {
                        setInternalSearchPhrase("");
                        navigate("/");
                    }}
                />

                <SearchResults
                    data={data}
                    onPageChange={handlePageChange}
                    searchPhrase={searchPhrase}
                />

                <Alert severity="info" icon={false} variant="filled">
                    <Stack color="text.secondary">
                        <Typography variant="h6">
                            {t(Translations.globalSearch.infoBox.title)}
                        </Typography>
                        <Typography
                            variant="caption"
                            sx={{ padding: "1rem 0" }}
                        >
                            {t(Translations.globalSearch.infoBox.subtitle)}
                        </Typography>
                        <Typography variant="caption">
                            {t(Translations.globalSearch.infoBox.contactName)}
                        </Typography>
                        <Typography variant="caption">
                            {t(Translations.globalSearch.infoBox.contactJobTitle)}
                        </Typography>
                        <Typography variant="caption" color="primary">
                            {t(Translations.globalSearch.infoBox.contactPhoneNumber)}
                        </Typography>
                        <Typography variant="caption" color="primary">
                            {t(Translations.globalSearch.infoBox.contactEmail)}
                        </Typography>
                    </Stack>
                </Alert>
            </>
        );
    };

    return (
        <PageContent
            title={t(Translations.pages.title.searchResults)}
            showBreadcrumbs={true}
        >
            <>{isLoading ? <KlasisLoader /> : renderSearchResults()}</>
        </PageContent>
    );
};

export default GlobalSearch;
